export const MENTION_ALL_TARGET = 'all';

const MENTION_TOKEN_PATTERN = /<@(\d+|all)>/g;

export function mentionToken(target: number | typeof MENTION_ALL_TARGET) {
  return `<@${target}>`;
}

export function mentionDisplay(name: string) {
  return `@${name}`;
}

export type PickedMention = { target: number | typeof MENTION_ALL_TARGET; name: string };

export function toMentionTokens(messageContent: string, picked: PickedMention[]) {
  // Ranges are claimed on the original text, so a token already written can't be re-matched (<@all> → <<@all>>).
  return findMentionRanges(messageContent, picked).reduceRight(
    (body, { start, end, target }) => body.slice(0, start) + mentionToken(target) + body.slice(end),
    messageContent
  );
}

export function findMentionRanges(messageContent: string, picked: PickedMention[]) {
  const ranges: { start: number; end: number; target: PickedMention['target'] }[] = [];
  const taken: boolean[] = new Array(messageContent.length).fill(false);

  for (const pick of [...picked].sort((a, b) => b.name.length - a.name.length)) {
    const display = mentionDisplay(pick.name);
    let from = 0;

    while (from <= messageContent.length - display.length) {
      const at = messageContent.indexOf(display, from);
      if (at < 0) break;

      const free = taken.slice(at, at + display.length).every((slot) => !slot);
      if (free) {
        for (let i = at; i < at + display.length; i++) taken[i] = true;
        ranges.push({ start: at, end: at + display.length, target: pick.target });
        break;
      }
      from = at + 1;
    }
  }

  return ranges.sort((a, b) => a.start - b.start);
}

export function openMention(messageContent: string) {
  if (!messageContent) return false;

  const index = messageContent.lastIndexOf('@');
  if (index < 0) return false;

  const charBefore = index === 0 ? '' : messageContent[index - 1];
  if (charBefore && !/\s/.test(charBefore)) return false;

  return !messageContent.slice(index).includes(' ');
}

export function mentionQuery(messageContent: string) {
  const index = messageContent.lastIndexOf('@');
  return index < 0 ? '' : messageContent.slice(index + 1);
}

export function applyMention(messageContent: string, token: string) {
  const index = messageContent.lastIndexOf('@');
  const before = index < 0 ? messageContent : messageContent.slice(0, index);
  return `${before}${token} `;
}

export function mentionsToText(body: string, nameOf: (target: number) => string | undefined, removedLabel: string) {
  return splitMentionSegments(body)
    .map((segment) => {
      if (segment.type === 'text') return segment.value;
      if (segment.target === MENTION_ALL_TARGET) return mentionDisplay(MENTION_ALL_TARGET);
      return mentionDisplay(nameOf(segment.target) ?? removedLabel);
    })
    .join('');
}

// A stored body back into the composer state a live mention leaves: `@Name` text plus the picks that let
// toMentionTokens turn it into tokens again on save. A member no longer known keeps the removed label as
// plain text and no pick, so it is sent back as text rather than as a token for the wrong name.
export function mentionsToComposer(
  body: string,
  nameOf: (target: number) => string | undefined,
  removedLabel: string
): { messageContent: string; pickedMentions: PickedMention[] } {
  const pickedMentions: PickedMention[] = [];

  const messageContent = splitMentionSegments(body)
    .map((segment) => {
      if (segment.type === 'text') return segment.value;

      const name = segment.target === MENTION_ALL_TARGET ? MENTION_ALL_TARGET : nameOf(segment.target);
      if (!name) return mentionDisplay(removedLabel);

      pickedMentions.push({ target: segment.target, name });
      return mentionDisplay(name);
    })
    .join('');

  return { messageContent, pickedMentions };
}

export type MentionSegment =
  | { type: 'text'; value: string }
  | { type: 'mention'; target: number | typeof MENTION_ALL_TARGET };

export function splitMentionSegments(body: string): MentionSegment[] {
  const segments: MentionSegment[] = [];
  let cursor = 0;

  const pattern = new RegExp(MENTION_TOKEN_PATTERN.source, 'g');
  let match = pattern.exec(body);

  while (match) {
    if (match.index > cursor) segments.push({ type: 'text', value: body.slice(cursor, match.index) });

    const target = match[1];
    segments.push({
      type: 'mention',
      target: target === MENTION_ALL_TARGET ? MENTION_ALL_TARGET : Number(target),
    });

    cursor = match.index + match[0].length;
    match = pattern.exec(body);
  }

  if (cursor < body.length) segments.push({ type: 'text', value: body.slice(cursor) });

  return segments;
}
